import {
  HOVER_IDLE_SAVE_DATA_VERSION,
  type HoverIdleSaveData,
} from "./hoverIdleSaveData";
import {
  isHovSkillHoldMs,
  isHovSkillId,
} from "@/features/hoverIdle/hov/hovSkillDefinitions";
import { isWallStartChargeMs } from "@/features/hoverIdle/wall/wallStartCharge";
import { isTimSkillId } from "@/features/hoverIdle/tim/timSkillDefinitions";
import { MPM_ASSIGNMENT_IDS } from "@/features/hoverIdle/mpm/mpmDefinitions";
import { MPM_PRESET_MAX_SLOT_COUNT } from "@/features/hoverIdle/mpm/mpmState";
import {
  NHOV_BRANCH_IDS,
  isNHovBranchId,
} from "@/features/hoverIdle/nHov/nHovBranches";
import {
  HOV_CELL_COUNT,
  TIM_CELL_COUNT,
} from "@/features/hoverIdle/core/hoverIdleState";
import {
  isBundleEntitlementKey,
  isRewardCodeId,
  PERMANENT_PURCHASE_DEFINITIONS,
} from "@/features/payment/paymentDefinitions";
import { migrateHoverIdleSaveData } from "./hoverIdleSaveMigrations";
import { isSupportedLocale } from "@/i18n/runtime";
import { deserializeHuge, isNonNegativeHuge } from "@/shared/numbers";
import { isHelpTopicId } from "@/features/help/helpTopics";
import { validateOfflineRewardSaveMetadata } from "./offlineRewardSaveMetadata";
import {
  DEFAULT_EXPLORATION_DEFINITION,
  EXPLORATION_MP_SLOT_SEQUENCE,
  isExplorationMpSlotId,
} from "@/features/hoverIdle/exploration/explorationDefinitions";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationBiomeId,
} from "@/features/hoverIdle/exploration/explorationTypes";

export type HoverIdleSaveDataResult =
  | {
      readonly ok: true;
      readonly data: HoverIdleSaveData;
    }
  | {
      readonly ok: false;
      readonly reason: string;
    };

export function serializeHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): string {
  return JSON.stringify(saveData);
}

export function deserializeHoverIdleSaveData(
  serialized: string,
): HoverIdleSaveDataResult {
  try {
    return decodeHoverIdleSaveData(JSON.parse(serialized) as unknown);
  } catch (error) {
    return fail(error instanceof Error ? error.message : "invalid JSON");
  }
}

export function decodeHoverIdleSaveData(
  value: unknown,
): HoverIdleSaveDataResult {
  const migrated = migrateHoverIdleSaveData(value);
  if (!migrated.ok) {
    return migrated;
  }

  return validateHoverIdleSaveData(migrated.data);
}

export function validateHoverIdleSaveData(
  value: unknown,
): HoverIdleSaveDataResult {
  if (!isRecord(value)) {
    return fail("save data must be an object");
  }

  if (value.version !== HOVER_IDLE_SAVE_DATA_VERSION) {
    return fail(`save data version must be ${HOVER_IDLE_SAVE_DATA_VERSION}`);
  }

  const economy = readRecord(value, "economy");
  if (typeof economy === "string") return fail(economy);

  const purchasedUpgrades = readRecord(economy, "purchasedUpgrades");
  if (typeof purchasedUpgrades === "string") return fail(purchasedUpgrades);

  const unlocks = readRecord(economy, "unlocks");
  if (typeof unlocks === "string") return fail(unlocks);

  const unlocksTabs = readRecord(unlocks, "tabs");
  if (typeof unlocksTabs === "string") return fail(unlocksTabs);

  const unlocksEffects = readRecord(unlocks, "effects");
  if (typeof unlocksEffects === "string") return fail(unlocksEffects);

  const unlocksEffectsHov = readRecord(unlocksEffects, "hov");
  if (typeof unlocksEffectsHov === "string") return fail(unlocksEffectsHov);

  const prestige = readRecord(economy, "prestige");
  if (typeof prestige === "string") return fail(prestige);

  const targetGrid = readRecord(economy, "targetGrid");
  if (typeof targetGrid === "string") return fail(targetGrid);

  const minHovGainUnlockCosts = readArray(
    economy,
    "minHovGainUnlockCostsByTargetIndex",
    "economy.minHovGainUnlockCostsByTargetIndex",
  );
  if (typeof minHovGainUnlockCosts === "string") {
    return fail(minHovGainUnlockCosts);
  }

  const minHovGainMilestoneTriggerCosts = readArray(
    economy,
    "minHovGainMilestoneTriggerCostsByMilestoneIndex",
    "economy.minHovGainMilestoneTriggerCostsByMilestoneIndex",
  );
  if (typeof minHovGainMilestoneTriggerCosts === "string") {
    return fail(minHovGainMilestoneTriggerCosts);
  }

  const timTargetGrid = readRecord(economy, "timTargetGrid");
  if (typeof timTargetGrid === "string") return fail(timTargetGrid);

  const hovTimBalance = readRecord(economy, "hovTimBalance");
  if (typeof hovTimBalance === "string") return fail(hovTimBalance);

  const hiddenGrowth = readRecord(economy, "hiddenGrowth");
  if (typeof hiddenGrowth === "string") return fail(hiddenGrowth);

  const mHov = readRecord(economy, "mHov");
  if (typeof mHov === "string") return fail(mHov);

  const nHov = readRecord(economy, "nHov");
  if (typeof nHov === "string") return fail(nHov);

  const nHovBranchExperienceByBranch = optionalReadRecord(
    nHov,
    "branchExperienceByBranch",
  );
  if (typeof nHovBranchExperienceByBranch === "string") {
    return fail(nHovBranchExperienceByBranch);
  }
  const nHovEffectBoundaryCaps = optionalReadRecord(nHov, "effectBoundaryCaps");
  if (typeof nHovEffectBoundaryCaps === "string") {
    return fail(nHovEffectBoundaryCaps);
  }
  const nHovContinuousEffectProgressByBranch = optionalReadRecord(
    nHov,
    "continuousEffectProgressByBranch",
  );
  if (typeof nHovContinuousEffectProgressByBranch === "string") {
    return fail(nHovContinuousEffectProgressByBranch);
  }
  const nHovBranchEffectOverrides = optionalReadRecord(
    nHov,
    "branchEffectOverrides",
  );
  if (typeof nHovBranchEffectOverrides === "string") {
    return fail(nHovBranchEffectOverrides);
  }

  const oHov = readRecord(economy, "oHov");
  if (typeof oHov === "string") return fail(oHov);

  const exploration = optionalReadRecord(economy, "exploration");
  if (typeof exploration === "string") return fail(exploration);

  const resetAutomation = optionalReadRecord(economy, "resetAutomation");
  if (typeof resetAutomation === "string") {
    return fail(resetAutomation);
  }
  const resetAutomationMinimumGameplayIntervalCarry =
    resetAutomation === null
      ? null
      : readRecord(resetAutomation, "minimumGameplayIntervalCarryMs");
  if (typeof resetAutomationMinimumGameplayIntervalCarry === "string") {
    return fail(resetAutomationMinimumGameplayIntervalCarry);
  }

  const hovAmplifier = optionalReadRecord(economy, "hovAmplifier");
  if (typeof hovAmplifier === "string") return fail(hovAmplifier);
  if (hovAmplifier !== null) {
    const entriesValue = hovAmplifier.entries;
    if (entriesValue !== undefined && !Array.isArray(entriesValue)) {
      return fail("economy.hovAmplifier.entries must be an array when present");
    }
  }

  const wall = optionalReadRecord(economy, "wall");
  if (typeof wall === "string") return fail(wall);

  const mpm = readRecord(economy, "mpm");
  if (typeof mpm === "string") return fail(mpm);

  const mpmAssignments = readRecord(mpm, "assignments");
  if (typeof mpmAssignments === "string") return fail(mpmAssignments);

  const mpmHovTargets = readRecord(mpm, "hovTargets");
  if (typeof mpmHovTargets === "string") return fail(mpmHovTargets);

  const mpmHovGainUpgrades = readRecord(mpm, "hovGainUpgrades");
  if (typeof mpmHovGainUpgrades === "string") {
    return fail(mpmHovGainUpgrades);
  }

  const mpmMhovExecution = optionalReadRecord(mpm, "mhovExecution");
  if (typeof mpmMhovExecution === "string") {
    return fail(mpmMhovExecution);
  }

  const mpmNhovExecution = optionalReadRecord(mpm, "nhovExecution");
  if (typeof mpmNhovExecution === "string") {
    return fail(mpmNhovExecution);
  }
  const mpmNhovExecutionConditions =
    mpmNhovExecution === null
      ? null
      : readRecord(mpmNhovExecution, "conditions");
  if (typeof mpmNhovExecutionConditions === "string") {
    return fail(mpmNhovExecutionConditions);
  }

  const mpmOhovExecution = optionalReadRecord(mpm, "ohovExecution");
  if (typeof mpmOhovExecution === "string") {
    return fail(mpmOhovExecution);
  }

  const mpmAmplifierPanels = optionalReadRecord(mpm, "amplifierPanels");
  if (typeof mpmAmplifierPanels === "string") {
    return fail(mpmAmplifierPanels);
  }
  const mpmAmplifierUpgrades = readRecord(mpm, "amplifierUpgrades");
  if (typeof mpmAmplifierUpgrades === "string") {
    return fail(mpmAmplifierUpgrades);
  }
  const mpmExploration = optionalReadRecord(mpm, "exploration");
  if (typeof mpmExploration === "string") return fail(mpmExploration);

  const settings = readRecord(value, "settings");
  if (typeof settings === "string") return fail(settings);

  const cumulativeStats = readRecord(value, "cumulativeStats");
  if (typeof cumulativeStats === "string") return fail(cumulativeStats);

  const payment = readRecord(value, "payment");
  if (typeof payment === "string") return fail(payment);

  const purchasedPermanentItems = readRecord(
    payment,
    "purchasedPermanentItems",
  );
  if (typeof purchasedPermanentItems === "string") {
    return fail(purchasedPermanentItems);
  }

  const skills = readRecord(value, "skills");
  if (typeof skills === "string") return fail(skills);

  const hovAcquiredIdsValidation = requireSkillIdArray(
    skills,
    "hovAcquiredIds",
    "skills.hovAcquiredIds",
    isHovSkillId,
  );
  if (hovAcquiredIdsValidation !== null) return fail(hovAcquiredIdsValidation);

  const timAcquiredIdsValidation = requireSkillIdArray(
    skills,
    "timAcquiredIds",
    "skills.timAcquiredIds",
    isTimSkillId,
  );
  if (timAcquiredIdsValidation !== null) return fail(timAcquiredIdsValidation);

  const achievements = readRecord(value, "achievements");
  if (typeof achievements === "string") return fail(achievements);

  const unlockedIdsValidation = requireStringArray(
    achievements,
    "unlockedIds",
    "achievements.unlockedIds",
  );
  if (unlockedIdsValidation !== null) return fail(unlockedIdsValidation);

  const seenIdsValidation = requireStringArray(
    achievements,
    "seenIds",
    "achievements.seenIds",
  );
  if (seenIdsValidation !== null) return fail(seenIdsValidation);

  const records = readRecord(value, "records");
  if (typeof records === "string") return fail(records);

  const recordsValidation = validateRecordsState(records);
  if (recordsValidation !== null) return fail(recordsValidation);

  const timestamps = readRecord(value, "timestamps");
  if (typeof timestamps === "string") return fail(timestamps);

  const offlineReward = readRecord(value, "offlineReward");
  if (typeof offlineReward === "string") return fail(offlineReward);
  const offlineRewardValidation =
    validateOfflineRewardSaveMetadata(offlineReward);
  if (offlineRewardValidation !== null) return fail(offlineRewardValidation);

  const validations: readonly (string | null)[] = [
    requireNonNegativeHugeSaveValue(economy, "hov", "economy.hov"),
    ...(wall === null
      ? []
      : [
          optionalPositiveFiniteNumber(
            wall,
            "amplificationWallBestClearMs",
            "economy.wall.amplificationWallBestClearMs",
          ),
        ]),
    requireNonNegativeHugeSaveValue(economy, "tim", "economy.tim"),
    requireFiniteNonNegativeNumber(economy, "fai", "economy.fai"),
    requireNonNegativeHugeSaveValue(
      economy,
      "hovGainPerSecond",
      "economy.hovGainPerSecond",
    ),
    requirePositiveHugeSaveValue(economy, "upgradeCost", "economy.upgradeCost"),
    requirePositiveHugeSaveValue(
      economy,
      "timGainPerSecond",
      "economy.timGainPerSecond",
    ),
    requireNonNegativeHugeSaveValue(
      hiddenGrowth,
      "hovGainBonus",
      "economy.hiddenGrowth.hovGainBonus",
    ),
    requireNonNegativeHugeSaveValue(
      hiddenGrowth,
      "timGainBonus",
      "economy.hiddenGrowth.timGainBonus",
    ),
    requireFiniteNonNegativeNumber(
      hiddenGrowth,
      "hovTimProgress",
      "economy.hiddenGrowth.hovTimProgress",
    ),
    requirePositiveHugeSaveValue(
      economy,
      "timUpgradeCost",
      "economy.timUpgradeCost",
    ),
    requireNonNegativeInteger(
      purchasedUpgrades,
      "hovGain",
      "economy.purchasedUpgrades.hovGain",
    ),
    requireNonNegativeInteger(
      purchasedUpgrades,
      "timGain",
      "economy.purchasedUpgrades.timGain",
    ),
    requireBoolean(unlocksTabs, "hovTarget", "economy.unlocks.tabs.hovTarget"),
    requireBoolean(
      unlocksTabs,
      "hovTimBalance",
      "economy.unlocks.tabs.hovTimBalance",
    ),
    requireBoolean(
      unlocksTabs,
      "hovTimGainBalance",
      "economy.unlocks.tabs.hovTimGainBalance",
    ),
    requireBoolean(unlocksTabs, "hovSkill", "economy.unlocks.tabs.hovSkill"),
    requireBoolean(
      unlocksEffectsHov,
      "gridIncrease1",
      "economy.unlocks.effects.hov.gridIncrease1",
    ),
    requireNonNegativeInteger(prestige, "count", "economy.prestige.count"),
    requireFiniteNonNegativeNumber(
      prestige,
      "points",
      "economy.prestige.points",
    ),
    requireGridTier(targetGrid, "gridTier", "economy.targetGrid.gridTier"),
    allowOptionalNHovThemeHue(
      targetGrid,
      "themeHue",
      "economy.targetGrid.themeHue",
    ),
    requireHovCellArray(targetGrid, "hovCells", "economy.targetGrid.hovCells"),
    requireNullableHugeSaveValueArray(
      minHovGainUnlockCosts,
      HOV_CELL_COUNT,
      "economy.minHovGainUnlockCostsByTargetIndex",
    ),
    requireNullableHugeSaveValueList(
      minHovGainMilestoneTriggerCosts,
      "economy.minHovGainMilestoneTriggerCostsByMilestoneIndex",
    ),
    requireGridTier(
      timTargetGrid,
      "gridTier",
      "economy.timTargetGrid.gridTier",
    ),
    requireTimCellArray(
      timTargetGrid,
      "timCells",
      "economy.timTargetGrid.timCells",
    ),
    requireHovTimBalanceConsistency(hovTimBalance, "economy.hovTimBalance"),
    requireBoolean(settings, "reduceMotion", "settings.reduceMotion"),
    optionalBoolean(settings, "lowPowerMode", "settings.lowPowerMode"),
    requireSupportedLocale(settings, "language", "settings.language"),
    requireAchievementNotificationMode(
      settings,
      "achievementNotification",
      "settings.achievementNotification",
    ),
    requireWallNotificationMode(
      settings,
      "wallNotificationMode",
      "settings.wallNotificationMode",
    ),
    requireBoolean(
      settings,
      "helpNotificationsEnabled",
      "settings.helpNotificationsEnabled",
    ),
    requireHelpTopicIdArray(
      settings,
      "readHelpTopicIds",
      "settings.readHelpTopicIds",
    ),
    requireHovSkillHoldDuration(
      settings,
      "hovSkillHoldDurationMs",
      "settings.hovSkillHoldDurationMs",
    ),
    optionalWallStartChargeDuration(
      settings,
      "wallStartChargeDurationMs",
      "settings.wallStartChargeDurationMs",
    ),
    requireBoolean(
      settings,
      "firstAccessTutorialCompleted",
      "settings.firstAccessTutorialCompleted",
    ),
    requireMHovAnimationSpeed(
      settings,
      "mHovAnimationSpeed",
      "settings.mHovAnimationSpeed",
    ),
    requireResetChargeDurationMs(
      settings,
      "mHovChargeDurationMs",
      "settings.mHovChargeDurationMs",
    ),
    requireNHovAnimationSpeed(
      settings,
      "nHovAnimationSpeed",
      "settings.nHovAnimationSpeed",
    ),
    requireResetChargeDurationMs(
      settings,
      "nHovChargeDurationMs",
      "settings.nHovChargeDurationMs",
    ),
    requireResetChargeDurationMs(
      settings,
      "oHovChargeDurationMs",
      "settings.oHovChargeDurationMs",
    ),
    // Additive setting: legacy saves omit it, restore defaults it to true.
    optionalBoolean(
      settings,
      "outsideResetBurstAnimationEnabled",
      "settings.outsideResetBurstAnimationEnabled",
    ),
    optionalShortOrNormal(
      settings,
      "hoverToggleDuration",
      "settings.hoverToggleDuration",
    ),
    optionalShortOrNormal(
      settings,
      "hoverChargeDuration",
      "settings.hoverChargeDuration",
    ),
    optionalAmplifierUpgradeGuardMs(
      settings,
      "amplifierUpgradeGuardMs",
      "settings.amplifierUpgradeGuardMs",
    ),
    optionalBoolean(
      settings,
      "hasSeenAvailableAmplifierUpgradeGuard",
      "settings.hasSeenAvailableAmplifierUpgradeGuard",
    ),
    optionalBoolean(
      settings,
      "hasSeenMHovButton",
      "settings.hasSeenMHovButton",
    ),
    optionalBoolean(
      settings,
      "hasSeenNHovButton",
      "settings.hasSeenNHovButton",
    ),
    requireNonNegativeInteger(mHov, "count", "economy.mHov.count"),
    requirePositiveHugeSaveValue(
      mHov,
      "hgMultiplier",
      "economy.mHov.hgMultiplier",
    ),
    requireNonNegativeHugeSaveValue(
      mHov,
      "currentLoopHovEarned",
      "economy.mHov.currentLoopHovEarned",
    ),
    requireNonNegativeHugeSaveValue(
      mHov,
      "maxHovSinceLastMHov",
      "economy.mHov.maxHovSinceLastMHov",
    ),
    optionalNonNegativeHugeSaveValue(
      mHov,
      "lastRewardBaseHov",
      "economy.mHov.lastRewardBaseHov",
    ),
    requireNonNegativeInteger(nHov, "count", "economy.nHov.count"),
    ...(nHovBranchExperienceByBranch !== null
      ? [
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "hgAmplify",
            "economy.nHov.branchExperienceByBranch.hgAmplify",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "hgDiscount",
            "economy.nHov.branchExperienceByBranch.hgDiscount",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "hngScale",
            "economy.nHov.branchExperienceByBranch.hngScale",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "cellScale",
            "economy.nHov.branchExperienceByBranch.cellScale",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "hgNHovEffectsToHng",
            "economy.nHov.branchExperienceByBranch.hgNHovEffectsToHng",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchExperienceByBranch,
            "cellChain",
            "economy.nHov.branchExperienceByBranch.cellChain",
          ),
        ]
      : []),
    ...(nHovEffectBoundaryCaps !== null
      ? [
          optionalBoolean(
            nHovEffectBoundaryCaps,
            "hgDiscountCostDivisor",
            "economy.nHov.effectBoundaryCaps.hgDiscountCostDivisor",
          ),
        ]
      : []),
    ...(nHovContinuousEffectProgressByBranch !== null
      ? [
          optionalBoolean(
            nHovContinuousEffectProgressByBranch,
            "hgAmplify",
            "economy.nHov.continuousEffectProgressByBranch.hgAmplify",
          ),
          optionalBoolean(
            nHovContinuousEffectProgressByBranch,
            "hgDiscount",
            "economy.nHov.continuousEffectProgressByBranch.hgDiscount",
          ),
          optionalBoolean(
            nHovContinuousEffectProgressByBranch,
            "hngScale",
            "economy.nHov.continuousEffectProgressByBranch.hngScale",
          ),
          optionalBoolean(
            nHovContinuousEffectProgressByBranch,
            "hgNHovEffectsToHng",
            "economy.nHov.continuousEffectProgressByBranch.hgNHovEffectsToHng",
          ),
        ]
      : []),
    ...(nHovBranchEffectOverrides !== null
      ? [
          optionalNonNegativeHugeSaveValue(
            nHovBranchEffectOverrides,
            "hgAmplify",
            "economy.nHov.branchEffectOverrides.hgAmplify",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchEffectOverrides,
            "hgDiscount",
            "economy.nHov.branchEffectOverrides.hgDiscount",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchEffectOverrides,
            "hngScale",
            "economy.nHov.branchEffectOverrides.hngScale",
          ),
          optionalNonNegativeHugeSaveValue(
            nHovBranchEffectOverrides,
            "hgNHovEffectsToHng",
            "economy.nHov.branchEffectOverrides.hgNHovEffectsToHng",
          ),
        ]
      : []),
    requireNonNegativeInteger(oHov, "count", "economy.oHov.count"),
    optionalBoolean(oHov, "isUnlocked", "economy.oHov.isUnlocked"),
    requireNonNegativeHugeSaveValue(oHov, "power", "economy.oHov.power"),
    requireNonNegativeHugeSaveValue(oHov, "hovCore", "economy.oHov.hovCore"),
    validateOHovRunRecords(oHov.records, "economy.oHov.records"),
    validateExplorationState(exploration, "economy.exploration"),
    ...(resetAutomationMinimumGameplayIntervalCarry !== null
      ? [
          requireFiniteNonNegativeNumber(
            resetAutomationMinimumGameplayIntervalCarry,
            "mHov",
            "economy.resetAutomation.minimumGameplayIntervalCarryMs.mHov",
          ),
          requireFiniteNonNegativeNumber(
            resetAutomationMinimumGameplayIntervalCarry,
            "nHov",
            "economy.resetAutomation.minimumGameplayIntervalCarryMs.nHov",
          ),
          requireFiniteNonNegativeNumber(
            resetAutomationMinimumGameplayIntervalCarry,
            "oHov",
            "economy.resetAutomation.minimumGameplayIntervalCarryMs.oHov",
          ),
        ]
      : []),
    ...("lastAnyResetAtMs" in economy
      ? [
          requireFiniteNonNegativeNumber(
            economy,
            "lastAnyResetAtMs",
            "economy.lastAnyResetAtMs",
          ),
        ]
      : []),
    ...MPM_ASSIGNMENT_IDS.map((assignmentId) =>
      requireNonNegativeInteger(
        mpmAssignments,
        assignmentId,
        `economy.mpm.assignments.${assignmentId}`,
      ),
    ),
    requireBoolean(
      mpmHovTargets,
      "unlocked",
      "economy.mpm.hovTargets.unlocked",
    ),
    requireBoolean(mpmHovTargets, "enabled", "economy.mpm.hovTargets.enabled"),
    requireArray(
      mpmHovTargets,
      "assignments",
      "economy.mpm.hovTargets.assignments",
    ),
    requireBoolean(
      mpmHovGainUpgrades,
      "unlocked",
      "economy.mpm.hovGainUpgrades.unlocked",
    ),
    requireBoolean(
      mpmHovGainUpgrades,
      "enabled",
      "economy.mpm.hovGainUpgrades.enabled",
    ),
    requireArray(
      mpmHovGainUpgrades,
      "assignments",
      "economy.mpm.hovGainUpgrades.assignments",
    ),
    ...(mpmMhovExecution === null
      ? []
      : [
          requireBoolean(
            mpmMhovExecution,
            "unlocked",
            "economy.mpm.mhovExecution.unlocked",
          ),
          requireBoolean(
            mpmMhovExecution,
            "enabled",
            "economy.mpm.mhovExecution.enabled",
          ),
          requireBoolean(
            mpmMhovExecution,
            "assigned",
            "economy.mpm.mhovExecution.assigned",
          ),
          requireNonNegativeHugeSaveValue(
            mpmMhovExecution,
            "multiplier",
            "economy.mpm.mhovExecution.multiplier",
          ),
          requireNonNegativeInteger(
            mpmMhovExecution,
            "minimumResetIntervalMs",
            "economy.mpm.mhovExecution.minimumResetIntervalMs",
          ),
        ]),
    ...(mpmNhovExecution === null
      ? []
      : [
          requireBoolean(
            mpmNhovExecution,
            "unlocked",
            "economy.mpm.nhovExecution.unlocked",
          ),
          requireBoolean(
            mpmNhovExecution,
            "enabled",
            "economy.mpm.nhovExecution.enabled",
          ),
          requireArray(
            mpmNhovExecution,
            "assignments",
            "economy.mpm.nhovExecution.assignments",
          ),
          requireMpmNHovExecutionConditions(
            mpmNhovExecutionConditions,
            "economy.mpm.nhovExecution.conditions",
          ),
        ]),
    ...(mpmOhovExecution === null
      ? []
      : [
          requireBoolean(
            mpmOhovExecution,
            "unlocked",
            "economy.mpm.ohovExecution.unlocked",
          ),
          requireBoolean(
            mpmOhovExecution,
            "enabled",
            "economy.mpm.ohovExecution.enabled",
          ),
          requireBoolean(
            mpmOhovExecution,
            "assigned",
            "economy.mpm.ohovExecution.assigned",
          ),
          requireNonNegativeHugeSaveValue(
            mpmOhovExecution,
            "minimumHovCore",
            "economy.mpm.ohovExecution.minimumHovCore",
          ),
          requireNonNegativeInteger(
            mpmOhovExecution,
            "minimumIntervalMs",
            "economy.mpm.ohovExecution.minimumIntervalMs",
          ),
        ]),
    ...(mpmAmplifierPanels === null
      ? []
      : [
          requireBoolean(
            mpmAmplifierPanels,
            "unlocked",
            "economy.mpm.amplifierPanels.unlocked",
          ),
          requireBoolean(
            mpmAmplifierPanels,
            "enabled",
            "economy.mpm.amplifierPanels.enabled",
          ),
          requireArray(
            mpmAmplifierPanels,
            "assignments",
            "economy.mpm.amplifierPanels.assignments",
          ),
        ]),
    requireBoolean(
      mpmAmplifierUpgrades,
      "unlocked",
      "economy.mpm.amplifierUpgrades.unlocked",
    ),
    requireBoolean(
      mpmAmplifierUpgrades,
      "enabled",
      "economy.mpm.amplifierUpgrades.enabled",
    ),
    requireArray(
      mpmAmplifierUpgrades,
      "assignments",
      "economy.mpm.amplifierUpgrades.assignments",
    ),
    optionalBoolean(mpm, "assignAllUnlocked", "economy.mpm.assignAllUnlocked"),
    validateMpmPresetSnapshots(mpm.presets, "economy.mpm.presets"),
    validateMpmExplorationState(mpmExploration, "economy.mpm.exploration"),
    validateExplorationAssignmentsAgainstPurchases(
      exploration,
      mpmExploration,
      "economy.mpm.exploration.assignedSlotIds",
    ),
    requireBoolean(
      economy,
      "isTimHovActivationEnabled",
      "economy.isTimHovActivationEnabled",
    ),
    requireNonNegativeHugeSaveValue(
      cumulativeStats,
      "totalHovEarned",
      "cumulativeStats.totalHovEarned",
    ),
    requireNonNegativeHugeSaveValue(
      cumulativeStats,
      "totalTimEarned",
      "cumulativeStats.totalTimEarned",
    ),
    requireFiniteNonNegativeNumber(
      cumulativeStats,
      "totalFaiEarned",
      "cumulativeStats.totalFaiEarned",
    ),
    requireNonNegativeHugeSaveValue(
      cumulativeStats,
      "totalTimSpent",
      "cumulativeStats.totalTimSpent",
    ),
    requireNonNegativeHugeSaveValue(
      cumulativeStats,
      "lifetimeHovRefinedFromTim",
      "cumulativeStats.lifetimeHovRefinedFromTim",
    ),
    requireNonNegativeInteger(
      cumulativeStats,
      "upgradesPurchased",
      "cumulativeStats.upgradesPurchased",
    ),
    // Additive record: legacy saves omit it, restore derives a safe fallback.
    optionalNonNegativeInteger(
      cumulativeStats,
      "hovGainFamilyUpgradesPurchased",
      "cumulativeStats.hovGainFamilyUpgradesPurchased",
    ),
    requireNonNegativeInteger(
      cumulativeStats,
      "amplifierUpgradesPurchased",
      "cumulativeStats.amplifierUpgradesPurchased",
    ),
    requireNonNegativeInteger(
      cumulativeStats,
      "prestigeCount",
      "cumulativeStats.prestigeCount",
    ),
    requireFiniteNonNegativeNumber(
      cumulativeStats,
      "onlinePlaytimeSeconds",
      "cumulativeStats.onlinePlaytimeSeconds",
    ),
    requireNonNegativeHugeSaveValue(
      cumulativeStats,
      "maxHov",
      "cumulativeStats.maxHov",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedNewHovWithoutMergeHov",
      "cumulativeStats.hasExecutedNewHovWithoutMergeHov",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedMergeHovWithOnlyOneHovTargetCell",
      "cumulativeStats.hasExecutedMergeHovWithOnlyOneHovTargetCell",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedNHovExceedingNotFullyFreeThreshold",
      "cumulativeStats.hasExecutedNHovExceedingNotFullyFreeThreshold",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedMassiveMergeHov",
      "cumulativeStats.hasExecutedMassiveMergeHov",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedOutsideMhov",
      "cumulativeStats.hasExecutedOutsideMhov",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "hasExecutedOutsideNhov",
      "cumulativeStats.hasExecutedOutsideNhov",
    ),
    // Additive record: legacy saves omit it, restore defaults it to false.
    optionalBoolean(
      cumulativeStats,
      "redAmplifierFullChargeReached",
      "cumulativeStats.redAmplifierFullChargeReached",
    ),
    // Additive record: legacy saves omit it, restore defaults it to "0".
    optionalNonNegativeHugeSaveValue(
      cumulativeStats,
      "totalHovCoreSpent",
      "cumulativeStats.totalHovCoreSpent",
    ),
    // Additive record: legacy saves omit it, restore defaults it to 0.
    optionalNonNegativeInteger(
      cumulativeStats,
      "mhovExecutedByMpCount",
      "cumulativeStats.mhovExecutedByMpCount",
    ),
    requireNullableEpochMilliseconds(
      timestamps,
      "firstPlayedAt",
      "timestamps.firstPlayedAt",
    ),
    requireNonNegativeInteger(payment, "token", "payment.token"),
    requireRewardCodeArray(
      payment,
      "redeemedRewardCodes",
      "payment.redeemedRewardCodes",
    ),
    requireEntitlementArray(payment, "entitlements", "payment.entitlements"),
    requireEntitlementArray(
      payment,
      "claimedEntitlements",
      "payment.claimedEntitlements",
    ),
    ...PERMANENT_PURCHASE_DEFINITIONS.map((definition) =>
      requireNonNegativeInteger(
        purchasedPermanentItems,
        definition.id,
        `payment.purchasedPermanentItems.${definition.id}`,
      ),
    ),
    requireEpochMilliseconds(timestamps, "savedAt", "timestamps.savedAt"),
  ];

  const failure = validations.find((result) => result !== null);
  if (failure !== undefined && failure !== null) {
    return fail(failure);
  }

  return {
    ok: true,
    data: value as unknown as HoverIdleSaveData,
  };
}

function requireMHovAnimationSpeed(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== "normal" && value !== "fast" && value !== "instant") {
    return `${path} must be "normal", "fast", or "instant"`;
  }
  return null;
}

function requireNHovAnimationSpeed(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== "normal" && value !== "fast" && value !== "instant") {
    return `${path} must be "normal", "fast", or "instant"`;
  }
  return null;
}

function requireResetChargeDurationMs(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== 0 && value !== 2000 && value !== 5000) {
    return `${path} must be 0, 2000, or 5000`;
  }
  return null;
}

function optionalAmplifierUpgradeGuardMs(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value === undefined) return null;
  if (value !== 0 && value !== 1000 && value !== 3000) {
    return `${path} must be 0, 1000, or 3000`;
  }
  return null;
}

function requireAchievementNotificationMode(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== "off" && value !== "dot" && value !== "full") {
    return `${path} must be "off", "dot", or "full"`;
  }
  return null;
}

function requireWallNotificationMode(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== "off" && value !== "dot" && value !== "full") {
    return `${path} must be "off", "dot", or "full"`;
  }
  return null;
}

function requireHovSkillHoldDuration(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (
    !Number.isInteger(value) ||
    typeof value !== "number" ||
    !isHovSkillHoldMs(value)
  ) {
    return `${path} must be a supported hold duration`;
  }
  return null;
}

function optionalWallStartChargeDuration(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value === undefined) return null;
  if (!isWallStartChargeMs(value)) {
    return `${path} must be a supported wall start charge duration`;
  }
  return null;
}

function requireSupportedLocale(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (typeof value !== "string" || !isSupportedLocale(value)) {
    return `${path} must be a supported locale`;
  }

  return null;
}

function fail(reason: string): HoverIdleSaveDataResult {
  return { ok: false, reason };
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validateExplorationState(
  state: Readonly<Record<string, unknown>> | null,
  path: string,
): string | null {
  if (state === null) return null;
  const areas = readRecord(state, "areas");
  if (typeof areas === "string") return `${path}.areas must be an object`;

  const headerValidations = [
    requirePositiveSafeInteger(state, "biomeLevel", `${path}.biomeLevel`),
    requireNonNegativeSafeInteger(
      state,
      "purchasedMpSlotCount",
      `${path}.purchasedMpSlotCount`,
    ),
    requireFiniteNonNegativeNumber(
      state,
      "cumulativeTimAddedMs",
      `${path}.cumulativeTimAddedMs`,
    ),
  ];
  const headerFailure = headerValidations.find((result) => result !== null);
  if (headerFailure !== undefined && headerFailure !== null) {
    return headerFailure;
  }
  if (
    (state.purchasedMpSlotCount as number) > EXPLORATION_MP_SLOT_SEQUENCE.length
  ) {
    return `${path}.purchasedMpSlotCount must identify a valid sequence prefix`;
  }
  if (
    state.timAccelerationEnabled !== undefined &&
    typeof state.timAccelerationEnabled !== "boolean"
  ) {
    return `${path}.timAccelerationEnabled must be a boolean`;
  }
  for (const key of Object.keys(areas)) {
    if (!EXPLORATION_BIOME_IDS.includes(key as ExplorationBiomeId)) {
      return `${path}.areas contains an unknown biome id: ${key}`;
    }
  }
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const area = readRecord(areas, biomeId);
    if (typeof area === "string") {
      return `${path}.areas.${biomeId} must be an object`;
    }
    const progressFailure = requireFiniteNonNegativeNumber(
      area,
      "progressMs",
      `${path}.areas.${biomeId}.progressMs`,
    );
    if (progressFailure !== null) return progressFailure;
    const progressMs = area.progressMs as number;
    const periodMs = DEFAULT_EXPLORATION_DEFINITION.areas[biomeId].periodMs;
    if (progressMs >= periodMs) {
      return `${path}.areas.${biomeId}.progressMs must be less than ${periodMs}`;
    }
  }
  return null;
}

function validateMpmExplorationState(
  state: Readonly<Record<string, unknown>> | null,
  path: string,
): string | null {
  if (state === null) return null;
  const enabledFailure = optionalBoolean(state, "enabled", `${path}.enabled`);
  if (enabledFailure !== null) return enabledFailure;
  const assignedSlotIds = readArray(
    state,
    "assignedSlotIds",
    `${path}.assignedSlotIds`,
  );
  if (typeof assignedSlotIds === "string") {
    return assignedSlotIds;
  }
  const seen = new Set<string>();
  for (const slotId of assignedSlotIds) {
    if (!isExplorationMpSlotId(slotId)) {
      return `${path}.assignedSlotIds contains an unknown slot id`;
    }
    if (seen.has(slotId)) {
      return `${path}.assignedSlotIds must not contain duplicates`;
    }
    seen.add(slotId);
  }
  return null;
}

function validateMpmPresetSnapshots(
  value: unknown,
  path: string,
): string | null {
  if (value === undefined) return null;
  if (!Array.isArray(value)) return `${path} must be an array`;
  if (value.length > MPM_PRESET_MAX_SLOT_COUNT) {
    return `${path} must contain at most ${MPM_PRESET_MAX_SLOT_COUNT} entries`;
  }

  for (let presetIndex = 0; presetIndex < value.length; presetIndex += 1) {
    const preset = value[presetIndex];
    if (preset === null) continue;
    if (!isRecord(preset)) {
      return `${path}[${presetIndex}] must be an object or null`;
    }
    const presetPath = `${path}[${presetIndex}]`;
    for (const key of [
      "hovTargets",
      "hovGainUpgrades",
      "amplifierPanels",
      "amplifierUpgrades",
      "nhovExecution",
    ] as const) {
      const section = readRecord(preset, key);
      if (typeof section === "string") {
        return `${presetPath}.${key} must be an object`;
      }
      const failure = [
        requireBoolean(section, "enabled", `${presetPath}.${key}.enabled`),
        requireArray(
          section,
          "assignments",
          `${presetPath}.${key}.assignments`,
        ),
      ].find((result) => result !== null);
      if (failure !== undefined && failure !== null) return failure;
    }
    for (const key of ["mhovExecution", "ohovExecution"] as const) {
      const section = readRecord(preset, key);
      if (typeof section === "string") {
        return `${presetPath}.${key} must be an object`;
      }
      const failure = [
        requireBoolean(section, "enabled", `${presetPath}.${key}.enabled`),
        requireBoolean(section, "assigned", `${presetPath}.${key}.assigned`),
      ].find((result) => result !== null);
      if (failure !== undefined && failure !== null) return failure;
    }
    const exploration = readRecord(preset, "exploration");
    if (typeof exploration === "string") {
      return `${presetPath}.exploration must be an object`;
    }
    const explorationFailure = [
      requireBoolean(
        exploration,
        "enabled",
        `${presetPath}.exploration.enabled`,
      ),
      requireArray(
        exploration,
        "assignedSlotIds",
        `${presetPath}.exploration.assignedSlotIds`,
      ),
    ].find((result) => result !== null);
    if (explorationFailure !== undefined && explorationFailure !== null) {
      return explorationFailure;
    }

    const numericSettingsValue = preset.numericSettings;
    if (numericSettingsValue === undefined || numericSettingsValue === null) {
      continue;
    }
    if (!isRecord(numericSettingsValue)) {
      return `${presetPath}.numericSettings must be an object or null`;
    }
    const mhovExecution = readRecord(numericSettingsValue, "mhovExecution");
    const nhovExecution = readRecord(numericSettingsValue, "nhovExecution");
    const ohovExecution = readRecord(numericSettingsValue, "ohovExecution");
    if (typeof mhovExecution === "string") {
      return `${presetPath}.numericSettings.mhovExecution must be an object`;
    }
    if (typeof nhovExecution === "string") {
      return `${presetPath}.numericSettings.nhovExecution must be an object`;
    }
    if (typeof ohovExecution === "string") {
      return `${presetPath}.numericSettings.ohovExecution must be an object`;
    }
    const conditions = readRecord(nhovExecution, "conditions");
    if (typeof conditions === "string") {
      return `${presetPath}.numericSettings.nhovExecution.conditions must be an object`;
    }
    const numericFailure = [
      requireNonNegativeHugeSaveValue(
        mhovExecution,
        "multiplier",
        `${presetPath}.numericSettings.mhovExecution.multiplier`,
      ),
      requireNonNegativeInteger(
        mhovExecution,
        "minimumResetIntervalMs",
        `${presetPath}.numericSettings.mhovExecution.minimumResetIntervalMs`,
      ),
      requireMpmNHovExecutionConditions(
        conditions,
        `${presetPath}.numericSettings.nhovExecution.conditions`,
      ),
      requireNonNegativeHugeSaveValue(
        ohovExecution,
        "minimumHovCore",
        `${presetPath}.numericSettings.ohovExecution.minimumHovCore`,
      ),
      requireNonNegativeInteger(
        ohovExecution,
        "minimumIntervalMs",
        `${presetPath}.numericSettings.ohovExecution.minimumIntervalMs`,
      ),
    ].find((result) => result !== null);
    if (numericFailure !== undefined && numericFailure !== null) {
      return numericFailure;
    }
  }
  return null;
}

function validateExplorationAssignmentsAgainstPurchases(
  exploration: Readonly<Record<string, unknown>> | null,
  mpmExploration: Readonly<Record<string, unknown>> | null,
  path: string,
): string | null {
  if (exploration === null || mpmExploration === null) return null;
  const purchasedCount = exploration.purchasedMpSlotCount;
  const assignedSlotIds = mpmExploration.assignedSlotIds;
  if (
    typeof purchasedCount !== "number" ||
    !Number.isSafeInteger(purchasedCount) ||
    purchasedCount < 0 ||
    purchasedCount > EXPLORATION_MP_SLOT_SEQUENCE.length ||
    !Array.isArray(assignedSlotIds)
  ) {
    return null;
  }
  const purchasedIds = new Set(
    EXPLORATION_MP_SLOT_SEQUENCE.slice(0, purchasedCount).map(
      (slot) => slot.id,
    ),
  );
  if (
    assignedSlotIds.some(
      (slotId) => isExplorationMpSlotId(slotId) && !purchasedIds.has(slotId),
    )
  ) {
    return `${path} must reference purchased slots`;
  }
  return null;
}

function requirePositiveSafeInteger(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0
    ? null
    : `${path} must be a positive safe integer`;
}

function requireNonNegativeSafeInteger(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? null
    : `${path} must be a non-negative safe integer`;
}

function readRecord(
  record: Readonly<Record<string, unknown>>,
  key: string,
): Readonly<Record<string, unknown>> | string {
  const value = record[key];
  if (!isRecord(value)) {
    return `${key} must be an object`;
  }

  return value;
}

function readArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string = key,
): readonly unknown[] | string {
  const value = record[key];
  if (!Array.isArray(value)) {
    return `${path} must be an array`;
  }
  return value;
}

function optionalReadRecord(
  record: Readonly<Record<string, unknown>>,
  key: string,
): Readonly<Record<string, unknown>> | null | string {
  const value = record[key];
  if (value === undefined) return null;
  if (!isRecord(value)) {
    return `${key} must be an object when present`;
  }
  return value;
}

function requireFiniteNonNegativeNumber(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return `${path} must be a finite non-negative number`;
  }

  return null;
}

function requireNonNegativeHugeSaveValue(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = deserializeHuge(record[key]);
  if (!isNonNegativeHuge(value)) {
    return `${path} must be a non-negative HugeNumber`;
  }

  return null;
}

function requirePositiveHugeSaveValue(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = deserializeHuge(record[key]);
  if (value.lte(0)) {
    return `${path} must be a positive HugeNumber`;
  }

  return null;
}

function requireNonNegativeInteger(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    return `${path} must be a non-negative integer`;
  }

  return null;
}

function requireNonNegativeFiniteNumber(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return `${path} must be a non-negative finite number`;
  }
  return null;
}

function requireMpmNHovExecutionConditions(
  record: Readonly<Record<string, unknown>> | null,
  path: string,
): string | null {
  if (record === null) {
    return `${path} must be an object`;
  }
  for (const key of Object.keys(record)) {
    if (!isNHovBranchId(key)) {
      return `${path} must contain valid NHov branch ids`;
    }
  }
  for (const branchId of NHOV_BRANCH_IDS) {
    const condition = readRecord(record, branchId);
    if (typeof condition === "string") {
      return `${path}.${branchId} must be an object`;
    }
    const effectFailure = requireNonNegativeHugeSaveValue(
      condition,
      "minimumEffect",
      `${path}.${branchId}.minimumEffect`,
    );
    if (effectFailure !== null) return effectFailure;
    const timeFailure = requireNonNegativeFiniteNumber(
      condition,
      "minimumTimeSeconds",
      `${path}.${branchId}.minimumTimeSeconds`,
    );
    if (timeFailure !== null) return timeFailure;
  }
  return null;
}

function optionalNonNegativeInteger(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  if (record[key] === undefined) return null;
  return requireNonNegativeInteger(record, key, path);
}

function optionalPositiveFiniteNumber(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value === undefined || value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return `${path} must be a finite positive number or null`;
  }
  return null;
}

function optionalShortOrNormal(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value === undefined || value === "short" || value === "normal") {
    return null;
  }
  return `${path} must be "short" or "normal" when present`;
}

function optionalNonNegativeHugeSaveValue(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  if (record[key] === undefined) return null;
  return requireNonNegativeHugeSaveValue(record, key, path);
}

function requireNullableHugeSaveValueList(
  value: readonly unknown[],
  path: string,
): string | null {
  for (let i = 0; i < value.length; i += 1) {
    const item = value[i];
    if (item === null) continue;
    const hugeValue = deserializeHuge(item);
    if (!isNonNegativeHuge(hugeValue)) {
      return `${path}[${i}] must be a non-negative HugeNumber or null`;
    }
  }
  return null;
}

function requireNullableHugeSaveValueArray(
  value: readonly unknown[],
  expectedLength: number,
  path: string,
): string | null {
  if (value.length !== expectedLength) {
    return `${path} must contain exactly ${expectedLength} entries`;
  }
  return requireNullableHugeSaveValueList(value, path);
}

function optionalBoolean(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  if (record[key] === undefined) return null;
  return requireBoolean(record, key, path);
}

function requireArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  if (!Array.isArray(record[key])) {
    return `${path} must be an array`;
  }
  return null;
}

function requireBoolean(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  if (typeof record[key] !== "boolean") {
    return `${path} must be a boolean`;
  }

  return null;
}

function requireStringArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (!Array.isArray(value)) {
    return `${path} must be an array`;
  }
  for (const item of value) {
    if (typeof item !== "string") {
      return `${path} must contain only strings`;
    }
  }
  return null;
}

function requireSkillIdArray<T extends string>(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
  predicate: (value: string) => value is T,
): string | null {
  const arrayValidation = requireStringArray(record, key, path);
  if (arrayValidation !== null) {
    return arrayValidation;
  }

  const value = record[key] as readonly string[];
  for (const item of value) {
    if (!predicate(item)) {
      return `${path} contains an unknown id: ${item}`;
    }
  }
  return null;
}

function requireHelpTopicIdArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const arrayValidation = requireStringArray(record, key, path);
  if (arrayValidation !== null) {
    return arrayValidation;
  }

  const value = record[key] as readonly string[];
  for (const item of value) {
    if (!isHelpTopicId(item)) {
      return `${path} contains an unknown id: ${item}`;
    }
  }
  return null;
}

function requireRewardCodeArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const arrayValidation = requireStringArray(record, key, path);
  if (arrayValidation !== null) {
    return arrayValidation;
  }

  const value = record[key] as readonly string[];
  for (const item of value) {
    if (!isRewardCodeId(item)) {
      return `${path} contains an unknown id: ${item}`;
    }
  }
  return null;
}

function requireEntitlementArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const arrayValidation = requireStringArray(record, key, path);
  if (arrayValidation !== null) {
    return arrayValidation;
  }

  const value = record[key] as readonly string[];
  for (const item of value) {
    if (!isBundleEntitlementKey(item)) {
      return `${path} contains an unknown id: ${item}`;
    }
  }
  return null;
}

function requireEpochMilliseconds(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return `${path} must be epoch milliseconds`;
  }

  return null;
}

function requireNullableEpochMilliseconds(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return `${path} must be epoch milliseconds or null`;
  }

  return null;
}

function requireGridTier(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (value !== 1 && value !== 4 && value !== 9) {
    return `${path} must be one of 1, 4, or 9`;
  }
  return null;
}

function allowOptionalNHovThemeHue(
  record: Readonly<Record<string, unknown>>,
  key: string,
  _path: string,
): string | null {
  const value = record[key];
  if (value === undefined || value === null) return null;
  return null;
}

const HOV_TIM_BALANCE_KEYS = [
  "total",
  "unassigned",
  "hovAllocation",
  "timAllocation",
  "hovGainAllocation",
  "timGainAllocation",
] as const;

function requireHovTimBalanceConsistency(
  record: Readonly<Record<string, unknown>>,
  path: string,
): string | null {
  for (const key of HOV_TIM_BALANCE_KEYS) {
    const failure = requireNonNegativeInteger(record, key, `${path}.${key}`);
    if (failure !== null) return failure;
  }
  const total = record.total as number;
  const sum =
    (record.unassigned as number) +
    (record.hovAllocation as number) +
    (record.timAllocation as number) +
    (record.hovGainAllocation as number) +
    (record.timGainAllocation as number);
  if (sum !== total) {
    return `${path} allocations must sum to total`;
  }
  return null;
}

function validateOHovRunRecords(value: unknown, path: string): string | null {
  if (value === undefined) return null;
  if (!isRecord(value)) {
    return `${path} must be an object`;
  }
  const startedAt = value.currentRunStartedAtMs;
  if (
    typeof startedAt !== "number" ||
    !Number.isFinite(startedAt) ||
    startedAt < 0
  ) {
    return `${path}.currentRunStartedAtMs must be a finite non-negative number`;
  }
  const lastDuration = value.lastRunDurationMs;
  if (
    lastDuration !== null &&
    (typeof lastDuration !== "number" ||
      !Number.isFinite(lastDuration) ||
      lastDuration < 0)
  ) {
    return `${path}.lastRunDurationMs must be a finite non-negative number or null`;
  }
  const fastestDuration = value.fastestRunDurationMs;
  if (
    fastestDuration !== null &&
    (typeof fastestDuration !== "number" ||
      !Number.isFinite(fastestDuration) ||
      fastestDuration < 0)
  ) {
    return `${path}.fastestRunDurationMs must be a finite non-negative number or null`;
  }
  const currentRunGameElapsed = value.currentRunGameElapsedMs;
  if (
    currentRunGameElapsed !== undefined &&
    (typeof currentRunGameElapsed !== "number" ||
      !Number.isFinite(currentRunGameElapsed) ||
      currentRunGameElapsed < 0)
  ) {
    return `${path}.currentRunGameElapsedMs must be a finite non-negative number`;
  }
  const fastestGameDuration = value.fastestRunGameDurationMs;
  if (
    fastestGameDuration !== undefined &&
    fastestGameDuration !== null &&
    (typeof fastestGameDuration !== "number" ||
      !Number.isFinite(fastestGameDuration) ||
      fastestGameDuration < 0)
  ) {
    return `${path}.fastestRunGameDurationMs must be a finite non-negative number or null`;
  }
  return null;
}

function validateRecordsState(
  record: Readonly<Record<string, unknown>>,
): string | null {
  const purchaseHistory = record.purchaseHistory;
  if (!Array.isArray(purchaseHistory)) {
    return "records.purchaseHistory must be an array";
  }
  for (let i = 0; i < purchaseHistory.length; i += 1) {
    const entry = purchaseHistory[i];
    if (!isRecord(entry)) {
      return `records.purchaseHistory[${i}] must be an object`;
    }
    if (
      typeof entry.timestamp !== "number" ||
      !Number.isFinite(entry.timestamp) ||
      entry.timestamp < 0
    ) {
      return `records.purchaseHistory[${i}].timestamp must be epoch milliseconds`;
    }
    if (typeof entry.productId !== "string") {
      return `records.purchaseHistory[${i}].productId must be a string`;
    }
  }

  const tokenTransactions = record.tokenTransactions;
  if (!Array.isArray(tokenTransactions)) {
    return "records.tokenTransactions must be an array";
  }
  for (let i = 0; i < tokenTransactions.length; i += 1) {
    const entry = tokenTransactions[i];
    if (!isRecord(entry)) {
      return `records.tokenTransactions[${i}] must be an object`;
    }
    if (
      typeof entry.timestamp !== "number" ||
      !Number.isFinite(entry.timestamp) ||
      entry.timestamp < 0
    ) {
      return `records.tokenTransactions[${i}].timestamp must be epoch milliseconds`;
    }
    if (typeof entry.delta !== "number" || !Number.isFinite(entry.delta)) {
      return `records.tokenTransactions[${i}].delta must be a finite number`;
    }
    if (entry.reason !== "tokenPack") {
      return `records.tokenTransactions[${i}].reason must be a known reason`;
    }
    if (entry.productId !== undefined && typeof entry.productId !== "string") {
      return `records.tokenTransactions[${i}].productId must be a string`;
    }
  }

  return null;
}

function requireHovCellArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (!Array.isArray(value)) {
    return `${path} must be an array`;
  }
  if (value.length !== HOV_CELL_COUNT) {
    return `${path} must contain exactly ${HOV_CELL_COUNT} cells`;
  }
  for (let i = 0; i < value.length; i += 1) {
    const cell = value[i];
    if (!isRecord(cell)) {
      return `${path}[${i}] must be an object`;
    }
    const validations: readonly (string | null)[] = [
      requireBoolean(cell, "unlocked", `${path}[${i}].unlocked`),
      requireNonNegativeHugeSaveValue(
        cell,
        "hovGainMultiplier",
        `${path}[${i}].hovGainMultiplier`,
      ),
      requirePositiveHugeSaveValue(
        cell,
        "upgradeCost",
        `${path}[${i}].upgradeCost`,
      ),
      requireNonNegativeInteger(
        cell,
        "purchasedUpgrades",
        `${path}[${i}].purchasedUpgrades`,
      ),
    ];
    const failure = validations.find((result) => result !== null);
    if (failure !== undefined && failure !== null) {
      return failure;
    }
  }
  return null;
}

function requireTimCellArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (!Array.isArray(value)) {
    return `${path} must be an array`;
  }
  if (value.length !== TIM_CELL_COUNT) {
    return `${path} must contain exactly ${TIM_CELL_COUNT} cells`;
  }
  for (let i = 0; i < value.length; i += 1) {
    const cell = value[i];
    if (!isRecord(cell)) {
      return `${path}[${i}] must be an object`;
    }
    const validations: readonly (string | null)[] = [
      requireBoolean(cell, "unlocked", `${path}[${i}].unlocked`),
      requireNonNegativeHugeSaveValue(
        cell,
        "timGainMultiplier",
        `${path}[${i}].timGainMultiplier`,
      ),
      requirePositiveHugeSaveValue(
        cell,
        "upgradeCost",
        `${path}[${i}].upgradeCost`,
      ),
      requireNonNegativeInteger(
        cell,
        "purchasedUpgrades",
        `${path}[${i}].purchasedUpgrades`,
      ),
    ];
    const failure = validations.find((result) => result !== null);
    if (failure !== undefined && failure !== null) {
      return failure;
    }
  }
  return null;
}
